import { v4 as uuid } from 'uuid';
import type { Newsletter, NewsletterSections } from '@/types';
import { ALL_SECTION_ORDER } from '@/types';
import { actionItemsStorage, newslettersStorage, versionsStorage } from '@/services/storage';
import { nowIso } from '@/utils/dateUtils';

const EMPTY_SECTIONS: NewsletterSections = {
  executiveSummary: '',
  keyAchievements: '',
  productUpdates: '',
  engineeringProgress: '',
  aiInnovations: '',
  customerSuccessStories: '',
  upcomingMilestones: '',
  risksChallenges: '',
  decisionsRequired: '',
  appendix: '',
};

export async function listNewsletters(): Promise<Newsletter[]> {
  const all = await newslettersStorage.getAll();
  return all.sort((a, b) => b.issueNumber - a.issueNumber);
}

export async function getNewsletter(id: string): Promise<Newsletter | undefined> {
  return newslettersStorage.getById(id);
}

export async function getLatestNewsletter(): Promise<Newsletter | undefined> {
  const all = await listNewsletters();
  return all[0];
}

/** Creates a fresh draft, auto-incrementing the issue number from the latest existing issue. */
export async function createDraftNewsletter(): Promise<Newsletter> {
  const latest = await getLatestNewsletter();
  const nextIssueNumber = (latest?.issueNumber ?? 0) + 1;
  const timestamp = nowIso();
  const newsletter: Newsletter = {
    id: uuid(),
    title: `PulseAI Monthly Pulse — Issue #${nextIssueNumber}`,
    issueNumber: nextIssueNumber,
    status: 'draft',
    periodStart: timestamp.slice(0, 10),
    periodEnd: timestamp.slice(0, 10),
    sections: { ...EMPTY_SECTIONS },
    kpis: [],
    enabledSections: [...ALL_SECTION_ORDER],
    currentVersion: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  await newslettersStorage.save(newsletter);
  return newsletter;
}

export async function saveNewsletter(newsletter: Newsletter): Promise<Newsletter> {
  const updated: Newsletter = { ...newsletter, updatedAt: nowIso() };
  await newslettersStorage.save(updated);
  return updated;
}

export async function publishNewsletter(newsletter: Newsletter): Promise<Newsletter> {
  const timestamp = nowIso();
  const updated: Newsletter = {
    ...newsletter,
    status: 'published',
    publishedAt: timestamp,
    updatedAt: timestamp,
  };
  await newslettersStorage.save(updated);
  return updated;
}

/** Removes a newsletter along with every action item and version snapshot that referenced it. */
export async function deleteNewsletter(id: string): Promise<void> {
  const [actionItems, versions] = await Promise.all([actionItemsStorage.getAll(), versionsStorage.getAll()]);
  for (const item of actionItems) {
    if (item.newsletterId === id) await actionItemsStorage.remove(item.id);
  }
  for (const version of versions) {
    if (version.newsletterId === id) await versionsStorage.remove(version.id);
  }
  await newslettersStorage.remove(id);
}
